import * as firestoreService from "./firestoreService";
import * as dexie from "../storage/dexie-db";
import { User, Template } from "../types/types";
// import { auth } from "../firebase";
import { TreeCheckboxSelectionKeys } from "primereact/tree";

export async function loadFlowData(
  flowId: string,
  useLocal: boolean
): Promise<any> {
  return useLocal
    ? dexie.loadFlowData(flowId)
    : firestoreService.loadFlowData(flowId);
}

export async function saveFlowData(
  flowId: string,
  data: any,
  useLocal: boolean
): Promise<{ success: boolean; error: any }> {
  return useLocal
    ? dexie.saveFlowData(flowId, data)
    : firestoreService.saveFlowData(flowId, data);
}

export async function loadFlowProps(
  flowId: string,
  useLocal: boolean
): Promise<any> {
  return useLocal
    ? dexie.loadFlowProps(flowId)
    : firestoreService.loadFlowProps(flowId);
}

export async function saveFlowProps(
  flowId: string,
  data: any,
  useLocal: boolean
): Promise<{ success: boolean; error: any }> {
  return useLocal
    ? dexie.saveFlowProps(flowId, data)
    : firestoreService.saveFlowProps(flowId, data);
}

export async function addCollaborator(
  flowId: string,
  owner: string,
  email: string,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.addCollaborator(flowId, owner, email)
    : firestoreService.addCollaborator(flowId, owner, email);
}

export async function removeCollaborator(
  flowId: string,
  email: string,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.removeCollaborator(flowId, email)
    : firestoreService.removeCollaborator(flowId, email);
}

export async function loadCollaborators(
  flowId: string,
  useLocal: boolean
): Promise<any[]> {
  return useLocal
    ? dexie.loadCollaborators(flowId)
    : firestoreService.loadCollaborators(flowId);
}

export async function fetchFlows(
  email: string,
  useLocal: boolean
): Promise<any[]> {
  return useLocal
    ? dexie.fetchFlows(email)
    : firestoreService.fetchFlows(email);
}

export async function addFeedback(
  email: string,
  feedback: string,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.addFeedback(email, feedback)
    : firestoreService.addFeedback(email, feedback);
}

export async function updateUserUseLocalData(
  userId: string,
  useLocalData: boolean
): Promise<void> {
  //return dexie.updateUserUseLocalData(userId, useLocalData);
  firestoreService.updateUseLocalData(userId, useLocalData);
}

export async function updateUserShowWelcome(
  userId: string,
  showWelcome: boolean,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.updateUserShowWelcome(userId, showWelcome)
    : firestoreService.updateUserShowWelcome(userId, showWelcome);
}

export async function updateUserInsights(
  userId: string,
  insights: TreeCheckboxSelectionKeys,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.updateUserInsights(userId, insights)
    : firestoreService.updateUserInsights(userId, insights);
}

export async function updateUserSettings(
  userId: string,
  settings: {
    presentWithShowHideButtons: boolean;
    currency: string;
    showWhyTag?: boolean;
    showStartupDialog?: boolean;
    showAIPromptOnNewMap?: boolean;
    aiModel?: string;
  },
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.updateUserSettings(userId, settings)
    : firestoreService.updateUserSettings(userId, settings);
}

export async function getUserDoc(
  userId: string,
  useLocal: boolean
): Promise<User | null> {
  const userDoc = useLocal
    ? await dexie.getUserDoc(userId)
    : await firestoreService.getUserDoc(userId);
  if (!userDoc) {
    return null;
  } else {
    if (!userDoc.insights) {
      userDoc.insights = {
        keys: [],
      } as TreeCheckboxSelectionKeys;
    }
    return userDoc as User;
  }
}

export async function checkUserPlanAndFlowLimit(
  userId: string,
  email: string,
  useLocal: boolean
): Promise<any> {
  return useLocal
    ? dexie.checkUserPlanAndFlowLimit(userId, email)
    : firestoreService.checkUserPlanAndFlowLimit(userId, email);
}

export async function getUserDataByEmail(
  email: string,
  useLocal: boolean
): Promise<any> {
  return useLocal
    ? dexie.getUserDataByEmail(email)
    : firestoreService.getUserDataByEmail(email);
}

export async function updateOrCreateUserDoc(
  user: any,
  useLocal: boolean,
  email?: string
): Promise<boolean> {
  return useLocal
    ? dexie.updateOrCreateUserDoc(user, email)
    : firestoreService.updateOrCreateUserDoc(user, email);
}

// Proxy method to get user plan
export async function getUserPlan(
  user: any,
  useLocal: boolean
): Promise<string | null> {
  if (useLocal) {
    return await dexie.getUserPlan(user);
  } else {
    return await firestoreService.getUserPlan(user);
  }
}

// Proxy method to get unlocked features
export async function getUnlockedFeatures(
  useLocal: boolean
): Promise<string[]> {
  if (useLocal) {
    return await dexie.getUnlockedFeatures();
  } else {
    return await firestoreService.getUnlockedFeatures();
  }
}

export async function deleteFlowData(
  flowId: string,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.deleteFlowData(flowId)
    : firestoreService.deleteFlowData(flowId);
}

export async function loadTemplate(
  id: string,
  useLocal: boolean
): Promise<any> {
  return useLocal ? dexie.loadTemplate(id) : firestoreService.loadTemplate(id);
}

export async function saveTemplate(
  flowId: string,
  data: any,
  useLocal: boolean
): Promise<{ success: boolean; error: any }> {
  return useLocal
    ? dexie.saveTemplate(flowId, data)
    : firestoreService.saveTemplate(flowId, data);
}

export async function deleteTemplate(
  id: string,
  useLocal: boolean
): Promise<void> {
  return useLocal
    ? dexie.deleteTemplate(id)
    : firestoreService.deleteTemplate(id);
}

export async function loadMyTemplates(
  email: string,
  useLocal: boolean
): Promise<Template[]> {
  return useLocal
    ? dexie.loadMyTemplates(email)
    : firestoreService.loadMyTemplates(email);
}

export async function getBusinessModuleSubscriber(
  email: string,
  id: string,
  useLocal: boolean
): Promise<boolean> {
  return useLocal
    ? false //dexie.getBusinessModuleSubscriber(email, id)
    : firestoreService.getBusinessModuleSubscriber(email, id);
}
